import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * OPTIMIZED VARIANT PICKER FOR 300+ VARIANTS
 * 
 * Changes from original:
 * 1. Added debouncing (50ms) to prevent rapid-fire fetch requests
 * 2. Added request queue management to handle rapid variant changes
 * 3. Improved AbortController usage
 * 4. Added variant data caching for repeated selections
 * 5. Dispatches legacy 'variant:change' event for compatibility with price blocks
 * 
 * @template {import('@theme/component').Refs} [Refs = {}]
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #debounceTimer;

  /** @type {Map<string, object>} */
  #variantCache = new Map();

  /** @type {HTMLElement | null} */
  #pendingTarget = null;

  /** @type {boolean} */
  #pendingLoadsNewProduct = false;

  // Debounce delay in milliseconds - adjust if needed
  static DEBOUNCE_MS = 50;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  disconnectedCallback() {
    // Clean up on disconnect
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }
    this.#abortController?.abort();
    this.#variantCache.clear();
  }

  /**
   * Handles the variant change event with debouncing.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Update UI immediately for responsiveness
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Store pending request info
    this.#pendingTarget = event.target;
    this.#pendingLoadsNewProduct = loadsNewProduct;

    // Update URL immediately for better UX
    const url = new URL(window.location.href);
    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }

    // Debounce the fetch request
    this.#debounceFetch();
  }

  /**
   * Debounces fetch requests to prevent rapid-fire API calls.
   * @private
   */
  #debounceFetch() {
    // Clear any pending debounce timer
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }

    // Abort any in-flight request
    this.#abortController?.abort();

    this.#debounceTimer = setTimeout(() => {
      if (this.#pendingTarget) {
        const requestUrl = this.buildRequestUrl(this.#pendingTarget);
        this.fetchUpdatedSection(requestUrl, this.#pendingLoadsNewProduct);
      }
    }, VariantPicker.DEBOUNCE_MS);
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section with improved error handling.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // Create new abort controller for this request
    this.#abortController = new AbortController();

    // Check cache first for variant data (not full HTML, just variant JSON)
    const cacheKey = this.selectedOptionsValues.join(',');

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Parse and cache variant data
        const variantData = JSON.parse(textContent);
        this.#variantCache.set(cacheKey, variantData);

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            // Dispatch the theme's custom event
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );

            // COMPATIBILITY: Also dispatch a standard 'variant:change' event
            // This ensures compatibility with custom price blocks and other components
            this.dispatchVariantChangeEvent(variantData);
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          // Request was intentionally aborted - this is expected during rapid clicking
          console.debug('[VariantPicker] Fetch aborted - newer request pending');
        } else {
          console.error('[VariantPicker] Fetch error:', error);
        }
      });
  }

  /**
   * Dispatches a standard variant:change event for compatibility with other components.
   * @param {object} variant - The variant data.
   * @private
   */
  dispatchVariantChangeEvent(variant) {
    // Dispatch on this element
    this.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant }
    }));

    // Also dispatch on document for global listeners
    document.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant }
    }));

    // Legacy event name some themes use
    document.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: { variant }
    }));
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
        // Clear cache when product changes
        this.#variantCache.clear();
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}